import { FC } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DailyNavRecordSecondaryBreakdown,
  DailyNavSecondaryProviderRow,
  DailyNavSecondaryProviderWindow,
  DailyNavSecondaryWindowSummary,
} from 'api';
import { dailyNavAsOfLabel } from 'common/constants/dailyNav';
import { shortDate } from 'utilities';
import ChangeFromMarkPanel from './ChangeFromMarkPanel';
import { formatPrice, formatSignedPercent, formatThreshold } from './formatters';
import { SecondaryBreakdownDialogProps } from './SecondaryBreakdownDialog.props';

// Keyed to match both the payload's `dates` block and each provider's per-window
// objects, so a value cannot land under the wrong date. Labels are built per
// breakdown because the window lengths are configured per firm.
const WINDOW_KEYS = [
  { key: 'short_window', dateKey: 'prior_short_window' },
  { key: 'long_window', dateKey: 'prior_long_window' },
  { key: 'since_last_valuation', dateKey: 'last_valuation' },
] as const;

// The session count for the fixed windows; the anchor date itself for the
// valuation window, whose length varies per company.
const windowHeading = (
  key: (typeof WINDOW_KEYS)[number]['key'],
  breakdown: DailyNavRecordSecondaryBreakdown
): { label: string; caption: string | null } => {
  if (key === 'short_window') {
    return {
      label: breakdown.short_window_sessions ? `${breakdown.short_window_sessions}-Day Δ` : 'Short Δ',
      caption: shortDate(breakdown.dates.prior_short_window) ?? null,
    };
  }
  if (key === 'long_window') {
    return {
      label: breakdown.long_window_sessions ? `${breakdown.long_window_sessions}-Day Δ` : 'Long Δ',
      caption: shortDate(breakdown.dates.prior_long_window) ?? null,
    };
  }
  return {
    label: 'Since Val Date Δ',
    // The valuation date is the anchor, and unlike the session windows it is
    // not derivable from a count -- so it belongs on the column itself.
    caption: breakdown.dates.last_valuation ? shortDate(breakdown.dates.last_valuation) : 'no valuation',
  };
};

// Rendered between the PRICED day and the recorded one, so a bare "that day"
// cannot be read as the recorded date. Empty where the instant speaks for itself.
const REVISION_QUALIFIERS: Record<string, string> = {
  prior_nav_day: '',
  report_day: 'not a NAV day',
  report_day_fallback: 'nothing recorded for this company that day',
  latest: '',
};

const revisionNote = (priorDate: string | null, window?: DailyNavSecondaryWindowSummary): string => {
  if (!window) {
    return '';
  }
  // Short date for the day priced, full date-time for the moment recorded --
  // the differing shapes are what keep the two apart at a glance.
  const pricedDay = shortDate(priorDate);
  // No prior date: the since-valuation column on a company that never had one.
  if (!pricedDay) {
    return '';
  }
  const recordedAt = dailyNavAsOfLabel(window.prior_snapshot_at);
  if (!recordedAt) {
    return `Price for ${pricedDay}, latest available record`;
  }
  const qualifier =
    window.prior_snapshot_source in REVISION_QUALIFIERS
      ? REVISION_QUALIFIERS[window.prior_snapshot_source]
      : window.prior_snapshot_source;
  return qualifier
    ? `Price for ${pricedDay} — ${qualifier}, so read as recorded ${recordedAt}`
    : `Price for ${pricedDay}, as recorded ${recordedAt}`;
};

// Why a company has no secondary data at all, phrased for a reviewer rather than
// as a raw enum. Distinguishing these matters: "not linked" is a setup gap
// someone can fix, "no data" is upstream.
const UNAVAILABLE_MESSAGES: Record<string, string> = {
  not_linked: 'This company is not matched to an external company profile, so no secondary providers can be resolved.',
  unresolved: 'This company’s external company profile is not recognised.',
  no_data: 'No secondary provider is mapped to this company, or the market-data service returned nothing.',
};

const EXCLUDED_REASONS: Record<string, string> = {
  no_data: 'Reported no price on any of these dates',
  unknown_currency: 'Reported a price with no currency, so it is left out of the average',
};

// A value whose own observation date is older than the date we asked for was
// carried forward by Alexandria. Surfacing it is the whole point of this dialog:
// it is what separates "the market did not move" from "this provider has not
// published since then", which otherwise both render as 0.0%.
const isStale = (asOfDate: string | null, requestedDate: string | null): boolean =>
  Boolean(asOfDate && requestedDate && asOfDate < requestedDate);

interface StaleMarkerProps {
  asOfDate: string | null;
  requestedDate: string | null;
}

const StaleMarker: FC<StaleMarkerProps> = ({ asOfDate, requestedDate }) => {
  if (!isStale(asOfDate, requestedDate)) {
    return null;
  }
  return (
    <Tooltip title={`Carried forward — this provider's latest observation is ${shortDate(asOfDate)}`}>
      <Typography component="span" variant="caption" sx={{ color: 'warning.main', ml: 0.5 }}>
        as of {shortDate(asOfDate)}
      </Typography>
    </Tooltip>
  );
};

// Both reasons the backend can discard a prior, keyed by slug so a new one shows
// up as a missing tooltip rather than silently taking the "no prior price" branch
// below -- which is how currency_unknown read until this map existed.
const WINDOW_EXCLUDED_REASONS: Record<string, string> = {
  currency_changed:
    'This provider reported the two dates in different currencies, so the change is not shown rather than computed across them',
  currency_unknown:
    'One of the two dates carries no reported currency, so the change is not shown rather than computed between values that may not be comparable',
};

// Why this window reads the way it does. A DISCARDED prior is not a missing one:
// saying "no prior price" about a price that exists in another currency is simply
// wrong, and wrong in the case most worth noticing.
const windowTooltip = (
  window: DailyNavSecondaryProviderWindow,
  currency: string | null,
  currentValue: string | null,
  currentAsOfDate: string | null
): string => {
  const discarded = WINDOW_EXCLUDED_REASONS[window.excluded_reason ?? ''];
  if (discarded) {
    return discarded;
  }
  if (window.prior_value == null) {
    return 'No prior price, so this window has no percent change';
  }
  return `${formatPrice(window.prior_value, currency)} on ${shortDate(window.prior_as_of_date)} → ${formatPrice(
    currentValue,
    currency
  )} on ${shortDate(currentAsOfDate)}`;
};

interface WindowCellProps {
  window: DailyNavSecondaryProviderWindow;
  requestedDate: string | null;
  currency: string | null;
  currentValue: string | null;
  currentAsOfDate: string | null;
}

// Spells out BOTH ends of the comparison. The current price is a few cells over,
// but a tooltip that says "→ current" makes the reader hold one number in their
// head while they go find the other.
const WindowCell: FC<WindowCellProps> = ({ window, requestedDate, currency, currentValue, currentAsOfDate }) => (
  <TableCell align="right">
    <Tooltip title={windowTooltip(window, currency, currentValue, currentAsOfDate)}>
      <Box>
        <Typography variant="body2">{formatSignedPercent(window.pct)}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {formatPrice(window.prior_value, currency)}
        </Typography>
        <StaleMarker asOfDate={window.prior_as_of_date} requestedDate={requestedDate} />
      </Box>
    </Tooltip>
  </TableCell>
);

const SecondaryBreakdownDialog: FC<SecondaryBreakdownDialogProps> = ({ row, open, onClose }) => {
  const breakdown = row?.record.secondary_breakdown;
  const providers = breakdown?.providers ?? [];

  const visibleNames = (names: string[]) => names;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth id="secondary-breakdown-dialog">
      <DialogTitle>
        <Typography variant="h6">Secondary Market Data{row?.companyName ? ` — ${row.companyName}` : ''}</Typography>
        {breakdown && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Current {shortDate(breakdown.dates.current)} · short {shortDate(breakdown.dates.prior_short_window)} · long{' '}
            {shortDate(breakdown.dates.prior_long_window)}
            {breakdown.dates.last_valuation ? ` · valuation ${shortDate(breakdown.dates.last_valuation)}` : ''}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {!breakdown && <Alert severity="info">No secondary breakdown is available for this row.</Alert>}

        {breakdown?.unavailable_reason && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {UNAVAILABLE_MESSAGES[breakdown.unavailable_reason] ?? breakdown.unavailable_reason}
          </Alert>
        )}

        {breakdown && providers.length > 0 && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Price</TableCell>
                  {WINDOW_KEYS.map(window => {
                    const heading = windowHeading(window.key, breakdown);
                    return (
                      <TableCell key={window.key} align="right">
                        <Typography variant="body2">{heading.label}</Typography>
                        {heading.caption && (
                          <Tooltip title={revisionNote(breakdown.dates[window.dateKey], breakdown[window.key])}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              from {heading.caption}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {providers.map((provider: DailyNavSecondaryProviderRow) => (
                  <TableRow
                    key={provider.provider_code}
                    sx={{ opacity: provider.excluded_reason ? 0.5 : 1 }}
                    id={`secondary-breakdown-provider-${provider.provider_code}`}>
                    <TableCell>
                      <Typography variant="body2">{provider.provider_code}</Typography>
                      {provider.excluded_reason && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {EXCLUDED_REASONS[provider.excluded_reason] ?? provider.excluded_reason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          provider.source_fetched_at
                            ? `Fetched by Alexandria ${shortDate(provider.source_fetched_at)}`
                            : ''
                        }>
                        <Typography variant="caption">{provider.price_model ?? '—'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {/* Exactly what the provider published. Nothing converts a
                          secondary price -- Alexandria owns that -- so this is
                          also the number every window percentage in this row was
                          computed from. */}
                      <Typography variant="body2">{formatPrice(provider.current_value, provider.currency)}</Typography>
                      {provider.current_value != null && !provider.currency && (
                        <Tooltip title="This provider reported no currency, so it is left out of the average rather than assumed to be the firm's.">
                          <Typography variant="caption" sx={{ color: 'warning.main', display: 'block' }}>
                            currency unknown
                          </Typography>
                        </Tooltip>
                      )}
                      <StaleMarker asOfDate={provider.current_as_of_date} requestedDate={breakdown.dates.current} />
                    </TableCell>
                    {WINDOW_KEYS.map(window => (
                      <WindowCell
                        key={window.key}
                        window={provider[window.key]}
                        requestedDate={breakdown.dates[window.dateKey]}
                        currency={provider.currency}
                        currentValue={provider.current_value}
                        currentAsOfDate={provider.current_as_of_date}
                      />
                    ))}
                  </TableRow>
                ))}

                {/* Average, and explicitly WHICH providers are in it. A provider
                    can drop out of Alexandria's response entirely at an earlier
                    revision, which would otherwise move the average for a reason
                    nobody could see from the grid. */}
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="body2" fontWeight={600}>
                      Average
                    </Typography>
                  </TableCell>
                  {/* The averaged price gets its own cell rather than being
                      merged into the label: it is the grid's "Avg. Price"
                      column, and the numerator of the mark comparison below. */}
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      {breakdown.price.mixed_currencies
                        ? '—'
                        : formatPrice(breakdown.price.average, breakdown.price.currency)}
                    </Typography>
                    {breakdown.price.mixed_currencies && (
                      <Tooltip title="These providers reported in different currencies. Nothing here converts them, so a mean would be a number in neither.">
                        <Typography variant="caption" sx={{ color: 'warning.main', display: 'block' }}>
                          mixed currencies
                        </Typography>
                      </Tooltip>
                    )}
                    <Tooltip
                      title={
                        visibleNames(breakdown.price.unknown_currency_providers).length
                          ? `In the average: ${visibleNames(breakdown.price.contributing_providers).join(', ') || 'none'}. Left out for reporting no currency: ${visibleNames(breakdown.price.unknown_currency_providers).join(', ')}.`
                          : visibleNames(breakdown.price.contributing_providers).join(', ') ||
                            'No provider reported a price'
                      }>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {visibleNames(breakdown.price.contributing_providers).length} of {providers.length} providers
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  {WINDOW_KEYS.map(window => {
                    const summary = breakdown[window.key];
                    return (
                      <TableCell key={window.key} align="right">
                        {/* A breached average reads red rather than carrying a
                            separate status row -- the number and its verdict are
                            the same fact, and splitting them made the reader
                            join two rows to learn one thing. */}
                        <Tooltip
                          title={
                            summary.exceeded
                              ? `Exceeds the ${summary.threshold_source} threshold of ${formatThreshold(summary)}`
                              : ''
                          }>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: summary.exceeded ? 'error.main' : 'inherit' }}>
                            {formatSignedPercent(summary.average)}
                          </Typography>
                        </Tooltip>
                        <Tooltip
                          title={visibleNames(summary.contributing_providers).join(', ') || 'No provider contributed'}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {visibleNames(summary.contributing_providers).length} of {providers.length} providers
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>

                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2">Threshold</Typography>
                  </TableCell>
                  {WINDOW_KEYS.map(window => (
                    <TableCell key={window.key} align="right">
                      <Typography variant="caption">{formatThreshold(breakdown[window.key])}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </>
        )}

        {/* Outside the providers block on purpose: an unlinked or no-data company
            still has a mark, and saying so is more useful than a blank panel. */}
        {breakdown?.change_from_mark && <ChangeFromMarkPanel changeFromMark={breakdown.change_from_mark} />}

        {breakdown && providers.length > 0 && (
          <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 2, mb: 0 }}>
            Current values are as reported at the cutoff time on the report date. Prices are compared with the prior
            price as was recorded here &mdash; so a provider restating or adjusting a historical price is not shown
            here. Hover a column&rsquo;s date to see when its price was recorded. For providers that publish after the
            cutoff time, or that don&rsquo;t report a price daily; the &ldquo;as of&rdquo; dates above indicate the date
            of the value used. Percent changes are averaged across providers; a provider with no usable prior is left
            out of that average.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} id="secondary-breakdown-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SecondaryBreakdownDialog;
